import { swedishPhoneNumber } from '../patterns/swedishPhoneNumber';
import { swedishCountryCode } from '../patterns/swedishCountryCode';
import { norwegianCountryCode } from '../patterns/norwegianCountryCode';
import { finnishCountryCode } from '../patterns/finnishCountryCode';
import { danishCountryCode } from '../patterns/danishCountryCode';
import { norwegianAreaMobile } from '../patterns/norwegianAreaMobile';
import { norwegianAreaCodeInvalid } from '../patterns/norwegianAreaCodeInvalid';
import { finnishAreaMobile } from '../patterns/finnishAreaMobile';
import { danishAreaMobile } from '../patterns/danishAreaMobile';
import { genericPhoneNumber } from '../patterns/genericPhoneNumber';
import { countries } from '../services/helpers/country';
import { validateRequiredUrlLines } from '../services/helpers/urlList';

const searchValidationConfig = {
    required: {
        value: false,
        message: 'Required field',
    },
};

const emailRegExp = new RegExp(
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
);

const emailValidationConfig = {
    required: {
        value: true,
        message: 'Required field',
    },
    emailRegExp: emailRegExp,
};

const emailOptionalValidationConfig = {
    pattern: {
        message: 'Please enter a valid email address on the format name@example.com',
        value: emailRegExp,
    },
};

const organizationNumberValueConfig = {
    required: {
        value: true,
        message: 'Required field',
    },
    pattern: {
        message:
            'Please enter a valid organization number containing only numbers and possibly "-"',
        value: /^[0-9\-]+$/,
    },
};

const conditionalEmailValidationConfig = (required: boolean) => ({
    required: {
        value: required,
        message: 'Required field',
    },
    pattern: {
        message: 'Please enter a valid email address on the format name@example.com',
        value: emailRegExp,
    },
});

const organizationNumberFormats: Record<string, { pattern: RegExp; hint: string }> = {
    SE: { pattern: /^\d{6}-?\d{4}$/, hint: 'Swedish format: ten digits as XXXXXX-XXXX.' },
    NO: { pattern: /^\d{9}$/, hint: 'Norwegian format: nine digits.' },
    FI: { pattern: /^\d{7}-\d$/, hint: 'Finnish format: XXXXXXX-X.' },
    DK: { pattern: /^\d{8}$/, hint: 'Danish format: eight digits (CVR).' },
};

const organizationNumberFormatHint = (countryCode: string): string =>
    organizationNumberFormats[countryCode]?.hint ?? '';

const countrySpecificOrganizationNumberValidationConfig = (countryCode: string) => {
    const format = organizationNumberFormats[countryCode];

    if (!format) return organizationNumberValueConfig;

    return {
        required: {
            value: true,
            message: 'Required field',
        },
        pattern: {
            message: format.hint,
            value: format.pattern,
        },
    };
};

/** A textarea with one https:// URL per line; blank lines are allowed, but at least one URL is required. */
const allowedUrlsValidationConfig = {
    required: {
        value: true,
        message: 'Required field',
    },
    validate: validateRequiredUrlLines,
};

const registrationNumberValueConfig = {
    required: {
        value: true,
        message: 'Required field',
    },
    pattern: {
        message:
            'Please enter a valid registration number containing only numbers and possibly "-"',
        value: /^[0-9a-z\-]+$/,
    },
};

const requiredValueConfig = {
    required: {
        value: true,
        message: 'Required field',
    },
};

const requireValueAsNumberConfig = {
    ...requiredValueConfig,
    valueAsNumber: true,
};

const requiredWithMinValue = {
    ...requireValueAsNumberConfig,
    min: {
        value: 1,
        message: 'Minimum input number is 1',
    },
};

const descriptionValidationConfig = {
    required: {
        value: true,
        message: 'Description is required',
    },
};

const articleNumberValidationConfig = {
    required: {
        value: true,
        message: 'Article number is required',
    },
};

const quantityValidationConfig = {
    required: {
        value: true,
        message: 'Quantity is required',
    },
    valueAsNumber: true,
    min: {
        value: 1,
        message: 'Minimum input number is 1',
    },
};

const priceValidationConfig = {
    required: {
        value: true,
        message: 'Price is required',
    },
    valueAsNumber: true,
};

const amountValidationConfig = {
    required: {
        value: true,
        message: 'Amount is required',
    },
    valueAsNumber: true,
};

const vatRateValidationConfig = {
    required: {
        value: true,
        message: 'VAT rate is required',
    },
    valueAsNumber: true,
};

const countrySpecificPhoneNumberValidationConfig = (countryCode: string) => {
    const phoneNrPrefix = countries[countryCode].phoneNrPrefix;
    return {
        validate: (value?: string) =>
            !value ||
            value.trim() === '' ||
            isValidMobilePhoneNumber(value) ||
            `Please enter a valid mobile phone number starting with either + or 00 followed by the country code, eg. ${
                phoneNrPrefix.length > 0 ? `+${phoneNrPrefix} or 00${phoneNrPrefix}` : '+46 or 0046'
            }`,
    };
};

export const isValidMobilePhoneNumber = (phoneNumber: string) => {
    if (swedishCountryCode.test(phoneNumber)) {
        return swedishPhoneNumber.test(phoneNumber);
    }

    if (norwegianCountryCode.test(phoneNumber)) {
        return norwegianAreaMobile.test(phoneNumber) && !norwegianAreaCodeInvalid.test(phoneNumber);
    }

    if (finnishCountryCode.test(phoneNumber)) {
        return finnishAreaMobile.test(phoneNumber);
    }

    if (danishCountryCode.test(phoneNumber)) {
        return danishAreaMobile.test(phoneNumber);
    }

    return genericPhoneNumber.test(phoneNumber);
};

/*
 Types setup for validation of 'react-hook-form'
 Here we can build functions, regex or length checks to validate inputs
 */
type RefReturn =
    | string
    | ((instance: HTMLInputElement | null) => void)
    | React.RefObject<HTMLInputElement>
    | null
    | undefined;

export type SearchValidationConfigType = ({
    required,
    maxLength,
    validate,
}: {
    required?: { value: boolean; message: string };
    maxLength?: number;
    validate?: (searchPhrase: string) => boolean | string;
}) => RefReturn;

export type EmailInviteValidationConfigType = ({
    required,
    emailRegExp,
    validate,
}: {
    required?: { value: boolean; message: string };
    emailRegExp?: RegExp;
    validate?: (email: string) => boolean | string;
}) => RefReturn;

export type InviteUserNameValidationConfigType = ({
    required,
    validate,
}: {
    required: { value: boolean; message: string };
    validate?: (groupName: string) => boolean | string;
}) => RefReturn;

export type GroupCreationValidationConfigType = ({
    required,
    validate,
}: {
    required: { value: boolean; message: string };
    validate?: (groupName: string) => boolean | string;
}) => RefReturn;

export {
    searchValidationConfig,
    emailValidationConfig,
    emailOptionalValidationConfig,
    conditionalEmailValidationConfig,
    organizationNumberValueConfig,
    organizationNumberFormatHint,
    countrySpecificOrganizationNumberValidationConfig,
    allowedUrlsValidationConfig,
    registrationNumberValueConfig,
    requiredValueConfig,
    requiredWithMinValue,
    requireValueAsNumberConfig,
    descriptionValidationConfig,
    articleNumberValidationConfig,
    quantityValidationConfig,
    priceValidationConfig,
    amountValidationConfig,
    vatRateValidationConfig,
    countrySpecificPhoneNumberValidationConfig,
};
